import { BookingEntity } from 'Models/Entities';
import { appConfigStore } from 'Models/AppConfigStore';
import { getMeasurementLabelFromId, getMeasurementValueFromId } from 'Util/_HumanWritten/MeasurementUtils';
import { CheckInBookingOverviewDto } from '../../CheckInEntities/CheckInBookingOverviewDto';
import { roundToDecimals } from 'Util/_HumanWritten/NumberUtils';
import { store } from 'Models/Store';
import { convertWeight, parseWeightUnit, roundWeightForUnit } from 'Util/Weight';
import { TripleMetric } from './VehicleTransformers';

function resolveTrailerWeightInDisplayUnit(
	exactWeight: number | null | undefined,
	exactWeightUnit: string | null | undefined,
	selectedWeightId: string | null | undefined,
): number {
	const displayUnit = appConfigStore.weightDisplayUnit;
	if (exactWeight != null) {
		const storedUnit = parseWeightUnit(exactWeightUnit, displayUnit);
		return roundWeightForUnit(convertWeight(Number(exactWeight), storedUnit, displayUnit), displayUnit);
	}
	const bandValueKg = getMeasurementValueFromId(selectedWeightId ?? undefined);
	return roundWeightForUnit(convertWeight(bandValueKg ?? 0, 'kg', displayUnit), displayUnit);
}

export function transformTrailerSpace(booking: CheckInBookingOverviewDto | BookingEntity, showWeight: boolean = false) {
	if (!booking || !booking.bookedSummary) {
		return null;
	}

	const { cargoInfo, towOnInfo } = booking.bookedSummary;
	if (!towOnInfo || !cargoInfo) {
		return null;
	}

	if (showWeight) {
		return resolveTrailerWeightInDisplayUnit(
			towOnInfo.exactWeight,
			towOnInfo.exactWeightUnit,
			towOnInfo.selectedWeightId,
		);
	}

	// The trailer uses its own width multiplier; when the vehicle/trailer widths are linked a
	// trailer with no width of its own falls back to the vehicle's width (matches the backend).
	const effectiveTrailerWidthId = appConfigStore.vehicleAndTrailerWidthLinked
		? (towOnInfo.selectedWidthId ?? cargoInfo.selectedWidthId)
		: towOnInfo.selectedWidthId;
	const multiplier = store.getWidthCapacityMultiplier(effectiveTrailerWidthId ?? '');
	const vehicleLength = getMeasurementValueFromId(towOnInfo.selectedLengthId);

	// prevent floating point number
	return roundToDecimals(multiplier * vehicleLength, 2);
}

export function transformTrailerAllMetrics(booking: CheckInBookingOverviewDto | BookingEntity): TripleMetric | null {
	if (!booking || !booking.bookedSummary) {
		return null;
	}

	const { cargoInfo, towOnInfo } = booking.bookedSummary;
	if (!towOnInfo || !cargoInfo) {
		return null;
	}

	const effectiveTrailerWidthId = appConfigStore.vehicleAndTrailerWidthLinked
		? (towOnInfo.selectedWidthId ?? cargoInfo.selectedWidthId)
		: towOnInfo.selectedWidthId;
	const multiplier = store.getWidthCapacityMultiplier(effectiveTrailerWidthId ?? '');
	const trailerSpace = getMeasurementValueFromId(towOnInfo.selectedLengthId);
	const trailerLength = getMeasurementLabelFromId(towOnInfo.selectedLengthId);
	const weight = resolveTrailerWeightInDisplayUnit(
		towOnInfo.exactWeight,
		towOnInfo.exactWeightUnit,
		towOnInfo.selectedWeightId,
	);

	return {
		length: trailerLength,
		spaces: roundToDecimals(multiplier * trailerSpace, 2),
		weight,
	};
}
